import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Textarea, useToast } from '@hiring-intelligence/ui';
import { interviewNotesApi } from '@/services/api';
import { Pencil, Trash2, Plus, X, Check } from 'lucide-react';
import { format } from 'date-fns';

interface InterviewNotesPanelProps {
	sourcedCandidateId: string;
	hiringRequestId: string;
	onNotesChange: () => void;
}

export function InterviewNotesPanel({ sourcedCandidateId, hiringRequestId, onNotesChange }: InterviewNotesPanelProps) {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const [newNote, setNewNote] = useState('');
	const [isAdding, setIsAdding] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editContent, setEditContent] = useState('');

	const { data: notesData, isLoading } = useQuery({
		queryKey: ['interview-notes', sourcedCandidateId],
		queryFn: () => interviewNotesApi.listByCandidate(sourcedCandidateId)
	});

	const createMutation = useMutation({
		mutationFn: () => interviewNotesApi.create({
			sourcedCandidateId,
			hiringRequestId,
			content: newNote
		}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['interview-notes', sourcedCandidateId] });
			onNotesChange();
			setNewNote('');
			setIsAdding(false);
			toast({ title: 'Note Added' });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	const updateMutation = useMutation({
		mutationFn: ({ id, content }: { id: string; content: string }) =>
			interviewNotesApi.update(id, { content }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['interview-notes', sourcedCandidateId] });
			setEditingId(null);
			toast({ title: 'Note Updated' });
		}
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => interviewNotesApi.remove(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['interview-notes', sourcedCandidateId] });
			onNotesChange();
			toast({ title: 'Note Deleted' });
		}
	});

	const notes = notesData?.data || [];

	return (
		<div className="space-y-4 pt-2">
			{/* Notes list */}
			{isLoading ? (
				<div className="flex items-center justify-center py-4">
					<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
				</div>
			) : notes.length === 0 && !isAdding ? (
				<p className="text-sm text-muted-foreground text-center py-4">
					No interview notes yet. Add notes from your conversations with this candidate.
				</p>
			) : (
				<div className="space-y-3">
					{notes.map((note: any) => (
						<div key={note.id} className="border rounded-md p-3">
							{editingId === note.id ? (
								<div className="space-y-2">
									<Textarea
										value={editContent}
										onChange={(e) => setEditContent(e.target.value)}
										className="min-h-[80px]"
									/>
									<div className="flex gap-2 justify-end">
										<Button
											variant="ghost"
											size="sm"
											onClick={() => setEditingId(null)}
										>
											<X className="h-3 w-3 mr-1" /> Cancel
										</Button>
										<Button
											size="sm"
											onClick={() => updateMutation.mutate({ id: note.id, content: editContent })}
											disabled={updateMutation.isPending || !editContent.trim()}
										>
											<Check className="h-3 w-3 mr-1" /> Save
										</Button>
									</div>
								</div>
							) : (
								<>
									<div className="flex items-start justify-between">
										<div className="flex-1">
											<p className="text-sm whitespace-pre-line">{note.content}</p>
										</div>
										<div className="flex gap-1 ml-2 shrink-0">
											<Button
												variant="ghost"
												size="sm"
												className="h-7 w-7 p-0"
												onClick={() => { setEditingId(note.id); setEditContent(note.content); }}
											>
												<Pencil className="h-3 w-3" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												className="h-7 w-7 p-0 text-destructive hover:text-destructive"
												onClick={() => deleteMutation.mutate(note.id)}
												disabled={deleteMutation.isPending}
											>
												<Trash2 className="h-3 w-3" />
											</Button>
										</div>
									</div>
									<div className="text-xs text-muted-foreground mt-2">
										{note.creator?.name || note.createdBy?.name || 'Unknown'} &middot; {format(new Date(note.createdAt), 'dd MMM yyyy HH:mm')}
										{note.updatedAt !== note.createdAt && ' (edited)'}
									</div>
								</>
							)}
						</div>
					))}
				</div>
			)}

			{/* Add new note */}
			{isAdding ? (
				<div className="space-y-2 border-t pt-3">
					<Textarea
						placeholder="Write your interview notes..."
						value={newNote}
						onChange={(e) => setNewNote(e.target.value)}
						className="min-h-[100px]"
						autoFocus
					/>
					<div className="flex gap-2 justify-end">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => { setIsAdding(false); setNewNote(''); }}
						>
							Cancel
						</Button>
						<Button
							size="sm"
							onClick={() => createMutation.mutate()}
							disabled={!newNote.trim() || createMutation.isPending}
						>
							{createMutation.isPending ? 'Saving...' : 'Add Note'}
						</Button>
					</div>
				</div>
			) : (
				<Button
					variant="outline"
					size="sm"
					onClick={() => setIsAdding(true)}
					className="w-full"
				>
					<Plus className="h-3 w-3 mr-1" /> Add Interview Note
				</Button>
			)}
		</div>
	);
}
